import type { Graphics } from 'pixi.js';
import { PAPER, lerpColor } from '../../color';
import type { Body, Kit, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const RUFFLES = 18;
const FROSTING = 16;
const RUNES = 4;
const CREASES = [0.15, 0.62, 1.1, 1.62, 2.2];
const CANDY = 10;
const pts: number[] = [];

function capsule(g: Graphics, x: number, y: number, len: number, rad: number, angle: number): void {
  const half = Math.max(0, len / 2 - rad);
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  pts.length = 0;
  for (const end of [1, -1]) {
    const cx = x + ux * half * end;
    const cy = y + uy * half * end;
    const from = angle + (end > 0 ? -Math.PI / 2 : Math.PI / 2);
    for (let j = 0; j <= 4; j++) {
      const a = from + (j * Math.PI) / 4;
      pts.push(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad);
    }
  }
  g.poly(pts, true);
}

function wavyRing(g: Graphics, b: Body, radius: number, lobes: number, amp: number, phase: number): void {
  const steps = lobes * 6;
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * TAU;
    const r = radius * (1 + amp * Math.sin(lobes * a + phase));
    const x = b.x + Math.cos(a) * r * b.scaleX;
    const y = b.y + Math.sin(a) * r * b.scaleY;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
}

function wrinkle(k: Kit, body: Body): number {
  const size = body.radius / Math.max(1, k.unit);
  return Math.max(0, Math.min(1, 1 - (size - 0.08) / 0.3));
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    g.ellipse(body.x - r * 0.06 * body.scaleX, body.y - r * 0.08 * body.scaleY, r * 0.62 * body.scaleX, r * 0.58 * body.scaleY).fill({
      color: lerpColor(color, PAPER, 0.4),
      alpha: 0.32 * alpha,
    });
    wavyRing(g, body, r * 0.84, RUFFLES, 0.035, 0);
    g.stroke({ width: Math.max(1, r * 0.07), color: lerpColor(color, pal.ink, 0.38), alpha: 0.42 * alpha, join: 'round' });
    k.highlight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.ellipse(body.x - r * 0.14 * body.scaleX, body.y - r * 0.16 * body.scaleY, r * 0.64 * body.scaleX, r * 0.58 * body.scaleY).fill({
      color: pal.heldLight,
      alpha: 0.24 * alpha,
    });
    const wr = wrinkle(k, body);
    if (wr > 0.02) {
      for (const base of CREASES) {
        const a = base + 0.1;
        const r0 = r * 0.6;
        const r1 = r * 0.9;
        const bend = 0.22 * wr;
        g.moveTo(body.x + Math.cos(a) * r0 * body.scaleX, body.y + Math.sin(a) * r0 * body.scaleY).quadraticCurveTo(
          body.x + Math.cos(a + bend) * (r0 + r1) * 0.5 * body.scaleX,
          body.y + Math.sin(a + bend) * (r0 + r1) * 0.5 * body.scaleY,
          body.x + Math.cos(a) * r1 * body.scaleX,
          body.y + Math.sin(a) * r1 * body.scaleY,
        );
      }
      g.stroke({ width: Math.max(1, r * 0.045), color: pal.heldShade, alpha: 0.75 * wr * alpha, cap: 'round' });
    }
    k.arcLine(body, r * 0.72, Math.PI + 0.35, Math.PI + 1.25);
    g.stroke({ width: Math.max(1.5, r * 0.09), color: pal.heldLight, alpha: 0.85 * alpha, cap: 'round' });
    const sa = Math.PI + 1.55;
    g.circle(body.x + Math.cos(sa) * r * 0.72 * body.scaleX, body.y + Math.sin(sa) * r * 0.72 * body.scaleY, Math.max(1, r * 0.05)).fill({
      color: pal.heldLight,
      alpha: 0.85 * alpha,
    });
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.4), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    const dot = Math.min(Math.max(2.5, k.unit * 0.018), ((reach * TAU) / FROSTING) * 0.48);
    k.ringDots(body, reach, FROSTING, spin, (x, y) => {
      g.circle(x, y, dot);
    });
    g.fill({ color: lerpColor(pal.incoming.circle, PAPER, 0.3), alpha: 0.6 * alpha });
    const len = Math.max(6, k.unit * 0.035);
    k.ringDots(body, reach + dot * 2.4, RUNES, Math.PI / 4 - spin * 0.6, (x, y, i, a) => {
      capsule(g, x, y, len, len * 0.24, a + Math.PI / 2);
      g.fill({ color: pal.sparks[i % pal.sparks.length] ?? pal.spot, alpha: 0.9 * alpha });
    });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.incoming.circle;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.18 * alpha });
    g.circle(x, y, radius * 1.1).fill({ color: on ? tone : lerpColor(pal.bg, pal.rim, 0.18), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    if (on) g.circle(x - radius * 0.38, y - radius * 0.38, radius * 0.32).fill({ color: PAPER, alpha: 0.7 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(7, k.unit * 0.04);
    let n = 0;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      const dir = Math.atan2(dy, dx);
      const segs = Math.min(80, Math.floor(len / step));
      for (let s = 1; s < segs; s++) {
        const t = s / segs;
        capsule(g, x0 + dx * t, y0 + dy * t, step * 0.6, 1.4, dir + (n++ % 2 === 0 ? 0.55 : -0.55));
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) g.circle(x, y, size * 0.95);
    else capsule(g, x, y, size * 2.6, size * 0.55, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const body: Body = { shape: 'circle', x: m.x, y: m.y, radius: m.r, rotation: 0, scaleX: 1, scaleY: 1 };
    wavyRing(g, body, m.r, 12, 0.06, m.grow * 2);
    g.stroke({ width: m.width, color: pal.incoming.circle, alpha: m.fade * 0.6, join: 'round' });
    const dot = m.unit * 0.013;
    for (let i = 0; i < CANDY; i++) {
      const a = (i / CANDY) * TAU + m.grow * 0.6;
      g.circle(m.x + Math.cos(a) * m.r * 1.22, m.y + Math.sin(a) * m.r * 1.22, dot).fill({
        color: pal.sparks[i % pal.sparks.length] ?? pal.spot,
        alpha: m.fade * 0.7,
      });
    }
  },

  trail(g, x, y, size, angle) {
    capsule(g, x, y, size * 2.4, size * 0.5, angle);
  },
};
